import React from "react";
import { Helmet } from "react-helmet";

/**
 * Centralized SEO/social head tags.
 *
 * Pass:
 *   - title:       page-specific (we append " | TextLink" automatically)
 *   - description: meta description, ~150-160 chars max
 *   - image:       Open Graph image URL (defaults to logo)
 *   - canonical:   absolute or path-relative URL of the canonical page
 *   - type:        OG type — usually "website" (default) or "article"
 *   - noIndex:     true to add <meta name="robots" content="noindex">
 *
 * If a prop is omitted, sensible defaults are used so every page gets
 * at least minimum SEO + share metadata.
 */

const SITE_URL = "https://textlinksms.com";
const DEFAULT_IMAGE = SITE_URL + "/assets/img/logo/logo2.png";
const DEFAULT_DESC =
  "TextLink turns any Android phone into an SMS gateway. Send messages through your own SIM from $0.001 per SMS — no 10DLC, no A2P registration, native GoHighLevel integration.";

const Head = ({
  title,
  description = DEFAULT_DESC,
  image = DEFAULT_IMAGE,
  canonical,
  type = "website",
  noIndex = false,
}) => {
  const fullTitle = title ? `${title} | TextLink` : "TextLink — Native SMS for your CRM";
  const canonicalUrl =
    canonical
      ? (canonical.startsWith("http") ? canonical : SITE_URL + canonical)
      : (typeof window !== "undefined" ? window.location.href : SITE_URL);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph (Facebook, LinkedIn, Discord, etc.) */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="TextLink" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {noIndex && <meta name="robots" content="noindex,nofollow" />}
    </Helmet>
  );
};

export default Head;
